import { ThemeProvider } from '@mui/material';
import useCurrentTenant from 'hooks/useCurrentTenant/useCurrentTenant';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import SyncingUsersAndFinishing from 'pages/SyncingUsersAndFinishing/SyncingUsersAndFinishing';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { tenantsServiceTokenVar } from 'state/vars';
import { lightMuiTheme } from 'theme/theme';
import Background from 'ui/atoms/Background/Background';
import Loader from 'ui/atoms/Loader/Loader';
import AcceptLegalDocumentsPage from 'ui/templates/AcceptLegalDocumentsPage/AcceptLegalDocumentsPage';
import AdminAccountCreatedPage from 'ui/templates/AdminAccountCreated/AdminAccountCreated';
import ChooseProviderPage from 'ui/templates/ChooseProvider/ChooseProvider';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import AlmostReady from 'ui/templates/ConnectEntraID/AlmostReady';
import ConnectEntraID from 'ui/templates/ConnectEntraID/ConnectEntraID';
import CreateAccountPage from 'ui/templates/CreateAccount/CreateAccount';
import InvalidOTR from 'ui/templates/InvalidOTR/InvalidOTR';
import VerifyAccountPage from 'ui/templates/VerifyAccount/VerifyAccount';
import WelcomePage from 'ui/templates/WelcomePage/WelcomePage';

const LicensePortalPage: React.FC<React.PropsWithChildren> = () => {
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get('id');
  useEffect(() => {
    tenantsServiceTokenVar(jwt);
    return () => {
      tenantsServiceTokenVar(null);
    };
  }, [jwt]);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });

  const { currentTenant } = useCurrentTenant({
    handleFetchError
  });

  const [isProcessStarted, setIsProcessStarted] = useState(false);

  const handleWelcomePageButtonOnClick = () => {
    setIsProcessStarted(true);
  };

  const getCurrentStep = () => {
    if (!currentTenant) {
      return 'Loading';
    }

    if (currentTenant.onboardingStep === 'AcceptLegalDocuments' && !isProcessStarted) return 'NotStarted';

    return currentTenant.onboardingStep;
  };

  switch (getCurrentStep()) {
    case 'NotStarted':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <WelcomePage handleButtonOnClick={handleWelcomePageButtonOnClick} />
        </ThemeProvider>
      );

    case 'Loading':
      return (
        <ComponentWrapper alignItems="center" justifyContent="center" width="100vw" height="100vh">
          <Background id="background" />
          <Loader />
        </ComponentWrapper>
      );

    case 'AcceptLegalDocuments':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <AcceptLegalDocumentsPage />
        </ThemeProvider>
      );

    case 'ChooseProvider':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <ChooseProviderPage />
        </ThemeProvider>
      );

    case 'ConnectEntra':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <ConnectEntraID />
        </ThemeProvider>
      );

    case 'LinkEntra':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <SyncingUsersAndFinishing />
        </ThemeProvider>
      );

    case 'CreateAccount':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <CreateAccountPage tenantId={currentTenant?.tenantId} />
        </ThemeProvider>
      );

    case 'VerifyAccount':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <VerifyAccountPage email={currentTenant?.additionalData} />;
        </ThemeProvider>
      );

    case 'TenantReadyEntra':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <AlmostReady />
        </ThemeProvider>
      );

    case 'TenantReadyCognito':
      return (
        <ThemeProvider theme={lightMuiTheme}>
          <AdminAccountCreatedPage />
        </ThemeProvider>
      );
  }
  return (
    <ThemeProvider theme={lightMuiTheme}>
      <InvalidOTR />
    </ThemeProvider>
  );
};
export default LicensePortalPage;
